import type * as React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ContentLayout from '@cloudscape-design/components/content-layout';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Spinner from '@cloudscape-design/components/spinner';
import Tabs from '@cloudscape-design/components/tabs';
import type { BillingSettings } from '@etiquetachef/types';
import {
  subscribeBillingSettings,
  subscribeEmailLogsByTenant,
  type DocResult,
  type EmailLogListResult,
} from '@/lib/firestoreTenantDetail';
import { BillingSettingsForm } from '@/features/billing/BillingSettingsForm';
import { EmailLogsTable } from '@/features/billing/EmailLogsTable';

type Tab = 'settings' | 'logs';

/**
 * Tela `/billing/emails`: configuração global dos e-mails de cobrança e
 * inspeção dos envios (`emailLogs`), em duas abas.
 *
 * Origem: sem issue — commit ae81f4d21, 2026-05-05 00:22 +0200 (Sprint 16 —
 * rotas /billing/* + sub-menu Cobrança).
 *
 * Por quê: a mesma tela cobre "o que mandamos" (toggles e janelas) e "o que
 * saiu de fato" (logs com status do SendGrid), para diagnosticar e-mail que
 * o cliente diz não ter recebido.
 *
 * Contrato: aba Configuração — `subscribeBillingSettings` (listener) e
 * `BillingSettingsForm`, que grava pela callable `updateBillingSettings`
 * (capability `updateBillingSettings`: ROOT e FINANCE,
 * `docs/permissions.md`). Aba Logs — `subscribeEmailLogsByTenant`, só
 * assinada com a aba aberta; filtro por `tenantId` aplicado no botão
 * (vazio = sem filtro), até 100 linhas `createdAt desc`.
 *
 * Usado por: `routes/router.tsx` (rota `billing/emails`, dentro do
 * `RequireSuperAdmin`), menu Cobrança → "Emails" (`AdminLayout`). Só
 * `apps/admin` (web). Spec: `docs/screens/billing-emails.md`.
 *
 * Arquitetura: leituras diretas autorizadas pelas regras
 * `match /config/billing/settings/{docId}` e `match /emailLogs/{idempotencyKey}`
 * (Lê: super-admin; Escreve: só servidor). O filtro por tenant usa o índice
 * composto `emailLogs (tenantId ASC, createdAt DESC)`.
 *
 * Dados: `config/billing/settings/singleton` (`BillingSettingsSchema` —
 * toggles booleanos por template e janelas em DIAS, ex.
 * `due_soon_days_before: [7, 3, 1]`); ausência do doc → estado `NOT_FOUND`.
 * Formato do log em `lib/emailLogs.ts`.
 *
 * Falhas conhecidas:
 *  - 2026-05-05 → 2026-10-03 (#795, ADM-5): o filtro por tenant consultava um
 *    campo inexistente sem índice e a aba Logs só mostrava erro (corrigido em
 *    `subscribeEmailLogsByTenant`).
 *
 * Issues: #795 (ADM-5, filtro dos logs).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function BillingEmailsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('settings');
  const [settings, setSettings] = useState<DocResult<BillingSettings>>({ kind: 'LOADING' });
  const [logs, setLogs] = useState<EmailLogListResult>({ kind: 'LOADING' });
  const [filterTenantId, setFilterTenantId] = useState('');
  const [committedFilter, setCommittedFilter] = useState<string | null>(null);

  useEffect(() => {
    setSettings({ kind: 'LOADING' });
    return subscribeBillingSettings(setSettings);
  }, []);

  useEffect(() => {
    if (tab !== 'logs') return;
    setLogs({ kind: 'LOADING' });
    return subscribeEmailLogsByTenant(committedFilter, setLogs);
  }, [tab, committedFilter]);

  const settingsContent = (
    <Box padding={{ top: 's' }}>
      {settings.kind === 'LOADING' && (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      )}
      {settings.kind === 'NOT_FOUND' && (
        <Box color="text-body-secondary">{t('billing.emails.settingsNotFound')}</Box>
      )}
      {settings.kind === 'ERROR' && <Alert type="error">{t('billing.emails.errorLoadSettings')}</Alert>}
      {settings.kind === 'READY' && <BillingSettingsForm settings={settings.data} />}
    </Box>
  );

  const logsContent = (
    <Box padding={{ top: 's' }}>
      <SpaceBetween size="m">
        <FormField label={t('billing.emails.filterByTenant')}>
          <SpaceBetween direction="horizontal" size="xs">
            <Input
              value={filterTenantId}
              onChange={({ detail }): void => {
                setFilterTenantId(detail.value);
              }}
              placeholder="tenant-id"
            />
            <Button
              onClick={(): void => {
                setCommittedFilter(filterTenantId.trim() === '' ? null : filterTenantId.trim());
              }}
            >
              {t('billing.emails.filterApply')}
            </Button>
          </SpaceBetween>
        </FormField>

        {logs.kind === 'LOADING' && (
          <Box textAlign="center" padding="l">
            <Spinner size="large" />
          </Box>
        )}
        {logs.kind === 'ERROR' && <Alert type="error">{t('billing.emails.errorLoadLogs')}</Alert>}
        {logs.kind === 'READY' && <EmailLogsTable entries={logs.entries} />}
      </SpaceBetween>
    </Box>
  );

  return (
    <ContentLayout
      header={
        <Header variant="h1" description={t('billing.emails.subtitle')}>
          {t('billing.emails.title')}
        </Header>
      }
    >
      <Tabs
        activeTabId={tab}
        onChange={({ detail }): void => {
          setTab(detail.activeTabId as Tab);
        }}
        tabs={[
          { id: 'settings', label: t('billing.emails.tabs.settings'), content: settingsContent },
          { id: 'logs', label: t('billing.emails.tabs.logs'), content: logsContent },
        ]}
      />
    </ContentLayout>
  );
}
